import React, { useEffect, useState } from "react";
import "./style.css";
import { message } from "antd";
import { getSensor } from "../../../API/sensor";
import Lottie from "lottie-react";
import reportGeneration from "../../../Assets/Animations/Report Generation.json";
import { useDateFilter } from "../../../Contexts/DateFilterContext";
import LineChart from "../../../Components/Charts/LineChart";
import BarChart from "../../../Components/Charts/BarChart";
import StackedBarChart from "../../../Components/Charts/StackedBarChart";
import GenericTable from "../../../Components/GenericTable";
import { BATTERY_UI_MAP } from "../../../Utils/BatteryStateMap";
import AreaChart from "../../../Components/Charts/AreaChart";

const Sensor = () => {
  const defaultSensorData = {
    vehiclevsfleet: {},
    tempvshumidity: { temp: [], humidity: [], labels: [] },
    movement: { movement: [], labels: [] },
    historical_tempvshumidity: { temp: [], humidity: [], labels: [] },
    door_open_close: [],
    board_voltage_data: [],
    highest_temp: 0,
    lowest_temp: { name: "", value: "" },
    highest_temp: { name: "", value: "" },
    lowest_humidity: { name: "", value: "" },
    highest_humidity: { name: "", value: "" },
    fleet_movement: 0,
    fleet_avg: "",
    total_open_count: 0,
    total_close_count: 0,
  };

  const [sensorData, setSensorData] = useState(defaultSensorData);
  const [loading, setLoading] = useState(true);
  const { from, to } = useDateFilter();

  useEffect(() => {
    if (!to || !from) {
      return;
    }

    const fetchSensor = async () => {
      try {
        setLoading(true);
        const response = await getSensor(to, from);

        setSensorData({
          ...defaultSensorData,
          ...response.data,
          tempvshumidity: {
            ...defaultSensorData.tempvshumidity,
            ...(response.data?.tempvshumidity || {}),
          },
          movement: {
            ...defaultSensorData.movement,
            ...(response.data?.movement || {}),
          },
          historical_tempvshumidity: {
            ...defaultSensorData.historical_tempvshumidity,
            ...(response.data?.historical_tempvshumidity || {}),
          },
        });
        setLoading(false);
      } catch (error) {
        message.error("Failed to fetch fuel data");
        console.log(error, "Failed to fetch fuel data");
        setLoading(false);
      }
    };

    fetchSensor();
  }, [to, from]);

  const voltageDataColumns = [
    {
      title: <span className="vehicle-title">Vehicle Name</span>,
      dataIndex: "name",
      key: "name",
      sorter: (a, b) =>
        a.name.localeCompare(b.name, undefined, {
          numeric: true,
        }),
    },
    {
      title: "Board Voltage (V)",
      dataIndex: "board_voltage",
      key: "board_voltage",
      sorter: (a, b) => a.board_voltage - b.board_voltage,
    },
    {
      title: <span className="vehicle-title">Percentage (SOC)</span>,
      dataIndex: "soc_percentage",
      key: "soc_percentage",
      render: (percentage) => <span>{percentage}%</span>,
      sorter: (a, b) =>
        a.soc_percentage.localeCompare(b.soc_percentage, undefined, {
          numeric: true,
        }),
    },
    {
      title: <span className="vehicle-title">Battery Voltage</span>,
      dataIndex: "battery_voltage",
      key: "battery_voltage",
      sorter: (a, b) =>
        a.battery_voltage.localeCompare(b.battery_voltage, undefined, {
          numeric: true,
        }),
    },
    {
      title: <span className="vehicle-title">Ignition State</span>,
      dataIndex: "ignition",
      key: "ignition",
      render: (ignition) => {
        const isOn = ignition === "On";
        return (
          <span className={isOn ? "text-green-class" : "text-red-class"}>
            {ignition ?? "Unknown"}
          </span>
        );
      },
      sorter: (a, b) =>
        a.ignition.localeCompare(b.ignition, undefined, {
          numeric: true,
        }),
    },
    {
      title: <span className="vehicle-title">Battery State</span>,
      dataIndex: "battery_recomendation",
      key: "battery_recomendation",
      render: (value) => {
        const ui = BATTERY_UI_MAP[value] || BATTERY_UI_MAP.unknown;

        return (
          <span className={`battery-state ${ui.className}`}>
            <span className={`status-dot ${ui.dot}`} />
            {ui.label}
          </span>
        );
      },
      sorter: (a, b) =>
        a.battery_recomendation.localeCompare(
          b.battery_recomendation,
          undefined,
          {
            numeric: true,
          }
        ),
    },
  ];

  return (
    <div className="sensor">
      {loading ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          className="loader-container"
        >
          <Lottie
            animationData={reportGeneration}
            loop={true}
            style={{ width: 150, height: 150 }}
          />
        </div>
      ) : (
        <div className="rows-container">
          <div className="row tabs">
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Fleet Average</span>
                <span className="value-box" style={{ color: "#1FC16B" }}>
                  {sensorData?.fleet_avg} KM
                </span>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">
                  Fleet Fuel Temperature & Humidity
                </span>
                <div className="row-data">
                  <div className="block">
                    <span className="value-box1">
                      {sensorData?.highest_temp?.value} °C
                    </span>
                    <span className="value-box2 ">
                      {sensorData?.highest_humidity?.value} %
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Fleet Movement</span>
                <span className="value-box" style={{ color: "#1FC16B" }}>
                  {sensorData?.fleet_movement}
                </span>
              </div>
            </div>
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Fleet Door Open/Close</span>
                <div className="row-data">
                  <div className="block">
                    <span className="value-box3">
                      {sensorData?.total_open_count}
                    </span>
                    <span className="value-box2 ">
                      {sensorData?.total_close_count}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row tabs">
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Lowest Temperature</span>
                <span className="value-box" style={{ color: "#1FC16B" }}>
                  {sensorData?.lowest_temp?.value}°C /{" "}
                  {sensorData?.lowest_temp?.name}
                </span>
              </div>
            </div>
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Highest Temperature</span>
                <span className="value-box" style={{ color: "#FB3748" }}>
                  {sensorData?.highest_temp?.value}°C /{" "}
                  {sensorData?.highest_temp?.name}
                </span>
              </div>
            </div>

            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Lowest Humidity</span>
                <span className="value-box" style={{ color: "#1FC16B" }}>
                  {sensorData?.lowest_humidity?.value}% /{" "}
                  {sensorData?.lowest_humidity?.name}
                </span>
              </div>
            </div>
            <div className="tab-item">
              <div className="left">
                <span className="tab-title">Highest Humidity</span>
                <span className="value-box" style={{ color: "#FB3748" }}>
                  {sensorData?.highest_humidity?.value}% /{" "}
                  {sensorData?.highest_humidity?.name}
                </span>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="graph">
              <BarChart
                graphData={sensorData?.vehiclevsfleet || {}}
                lengthKeys={2}
                colourTheme={["#DFB400"]}
                graphTitle="Vehicle vs Fleet Comparison"
                selectTenByDefault={false}
                units={["km"]}
                classNameCSS="vehicle-vs-fleet"
              />
            </div>
            <div className="graph">
              <BarChart
                graphData={sensorData?.movement || {}}
                lengthKeys={1}
                colourTheme={["#155DFC"]}
                graphTitle="Movement"
                selectTenByDefault={false}
                units={["%"]}
                classNameCSS="movement"
              />
            </div>
          </div>
          <div className="row">
            <div className="graph">
              <AreaChart
                graphData={sensorData?.tempvshumidity || {}}
                lengthKeys={2}
                colourTheme={["#DFB400", "#155DFC"]}
                graphTitle="Temp vs Humidity"
                selectTenByDefault={false}
                units={["°C", "%"]}
                classNameCSS="temp-vs-humidity"
              />
            </div>
            {/* <div className="graph">
              <LineChart
                graphData={sensorData?.historical_tempvshumidity || {}}
                lengthKeys={2}
                colourTheme={["#FB3748", "#155DFC"]}
                graphTitle="Historical Temp./Humid."
                selectTenByDefault={false}
                units={["°C", "°C"]}
                classNameCSS="historical-temp"
              />
            </div> */}
          </div>

          <GenericTable
            title="Board Voltage"
            dataSource={sensorData?.board_voltage_data}
            columns={voltageDataColumns}
          />
        </div>
      )}
    </div>
  );
};

export default Sensor;
